import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { handleGlobalPayment } from '../utils/paymentService';
import {
  ArrowLeft,
  RefreshCcw,
  ChevronRight,
  CheckCircle2,
  MapPin,
  User,
  Search,
  IndianRupee,
  Ruler,
  Package
} from 'lucide-react';

// ============================================================
// n8n Production Webhook
// ============================================================

const BACKEND_BASE_URL =
  'https://copper-focus-leading-thereby.trycloudflare.com/webhook/apni-manzil-logistics';

const CourierServiceDetail = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState(null);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [rateCheckLoading, setRateCheckLoading] = useState(false);

  const [formData, setFormData] = useState({
    serviceType: 'Domestic Courier',

    senderName: '',
    senderPhone: '',
    senderAddress: '',

    pickupPincode: '',
    pickupCity: '',
    pickupState: '',

    receiverName: '',
    receiverPhone: '',
    receiverAddress: '',

    dropPincode: '',
    dropCity: '',
    dropState: '',

    weight: '0.5',

    length: '10',
    breadth: '10',
    height: '10',

    parcelType: 'Non-Document',

    productDescription: '',
    declaredValue: '',

    paymentMode: 'Prepaid'
  });

  // ============================================================
  // INPUT CHANGE
  // ============================================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // ============================================================
  // PINCODE → CITY / STATE
  // ============================================================

  const handlePincodeChange = async (e, type) => {
    const val = e.target.value
      .replace(/\D/g, '')
      .slice(0, 6);

    if (type === 'pickup') {
      setFormData((prev) => ({
        ...prev,
        pickupPincode: val
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        dropPincode: val
      }));
    }

    if (val.length !== 6) {
      return;
    }

    try {
      const response = await axios.get(
        `https://api.postalpincode.in/pincode/${val}`
      );

      const data = response?.data?.[0];

      if (
        data?.Status === 'Success' &&
        Array.isArray(data?.PostOffice) &&
        data.PostOffice.length > 0
      ) {
        const postOffice = data.PostOffice[0];

        const cityName = postOffice?.District || '';
        const stateName = postOffice?.State || '';

        if (type === 'pickup') {
          setFormData((prev) => ({
            ...prev,
            pickupCity: cityName,
            pickupState: stateName
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            dropCity: cityName,
            dropState: stateName
          }));
        }
      }
    } catch (error) {
      console.error(
        'Error fetching city/state from pincode:',
        error
      );
    }
  };

  // ============================================================
  // CHECK LIVE RATES
  // ============================================================

  const handleCheckLiveRates = async () => {
    if (
      !formData.pickupPincode ||
      !formData.dropPincode ||
      !formData.weight
    ) {
      alert(
        'Please fill in the pickup pincode, delivery pincode, and weight!'
      );
      return;
    }

    setRateCheckLoading(true);
    setRates(null);
    setSelectedCourier(null);

    const rateRequestData = {
      action: 'check_rates',

      mobile: formData.senderPhone || '9999999999',

      pickup_pincode: formData.pickupPincode,
      pickup_city: formData.pickupCity,
      pickup_state: formData.pickupState,

      delivery_pincode: formData.dropPincode,
      delivery_city: formData.dropCity,
      delivery_state: formData.dropState,

      weight: parseFloat(formData.weight),

      length: parseFloat(formData.length) || 10,
      breadth: parseFloat(formData.breadth) || 10,
      height: parseFloat(formData.height) || 10,

      payment_mode:
        formData.paymentMode === 'COD'
          ? 'cod'
          : 'prepaid',

      product_description:
        formData.productDescription,

      declared_value:
        parseFloat(formData.declaredValue) || 0,

      parcel_type:
        formData.parcelType,

      timestamp: new Date().toISOString()
    };

    try {
      // Save inquiry
      await addDoc(
        collection(db, 'courier_inquiries'),
        rateRequestData
      );

      // Fetch live rates
      const response = await axios.post(
        BACKEND_BASE_URL,
        rateRequestData
      );

      console.log(
        '📦 LIVE RATE RESPONSE:',
        response.data
      );

      const availableCouriers =
        response?.data?.data?.available_courier_companies ||
        response?.data?.available_courier_companies ||
        response?.data?.data ||
        response?.data;

      if (
        Array.isArray(availableCouriers) &&
        availableCouriers.length > 0
      ) {
        setRates(availableCouriers);
      } else {
        alert(
          'Currently no service available for this route or weight.'
        );

        setRates(null);
      }

    } catch (error) {
      console.error(
        '❌ Rate Check Error:',
        error
      );

      console.error(
        '❌ Rate API Response:',
        error?.response?.data
      );

      alert(
        'An error occurred while checking rates.'
      );

      setRates(null);

    } finally {
      setRateCheckLoading(false);
    }
  };

  // ============================================================
  // FINAL BOOKING
  // ============================================================

  const handleFinalBooking = async (e) => {
    e.preventDefault();

    if (!selectedCourier) {
      alert(
        'Please select a courier service from the list!'
      );
      return;
    }

    if (
      !formData.senderName ||
      !formData.senderPhone ||
      !formData.senderAddress ||
      !formData.pickupPincode ||
      !formData.receiverName ||
      !formData.receiverPhone ||
      !formData.receiverAddress ||
      !formData.dropPincode
    ) {
      alert(
        'Please fill in all sender and receiver information.'
      );
      return;
    }

    const courierRate =
      selectedCourier.rate ??
      selectedCourier.price;

    const totalAmount =
      Math.ceil(parseFloat(courierRate) + 20);

    // ==========================================================
    // PREPAID
    // ==========================================================

    if (formData.paymentMode === 'Prepaid') {
      handleGlobalPayment({
        amount: totalAmount,

        serviceName:
          `${selectedCourier.courier_name ||
            selectedCourier.courierName} Courier Service`,

        customerName:
          formData.senderName,

        customerPhone:
          formData.senderPhone,

        onSuccess: (paymentId) => {
          // Payment success is NOT booking success.
          executeBookingOrder(paymentId);
        },

        onFailure: (error) => {
          console.error(
            'RAZORPAY FULL ERROR:',
            JSON.stringify(
              error,
              null,
              2
            )
          );

          alert(
            `Payment Failed\n\n` +
            `Code: ${error?.code || 'N/A'}\n` +
            `Description: ${error?.description || 'N/A'}\n` +
            `Reason: ${error?.reason || 'N/A'}`
          );
        }
      });

    } else {

      // ========================================================
      // COD
      // ========================================================

      executeBookingOrder('COD');
    }
  };

  // ============================================================
  // CREATE NIMBUSPOST ORDER
  // ============================================================

  const executeBookingOrder = async (paymentId) => {
    setLoading(true);

    try {
      const courierId =
        selectedCourier?.courier_company_id ||
        selectedCourier?.courierId;

      const courierName =
        selectedCourier?.courier_name ||
        selectedCourier?.courierName;

      const courierRate =
        selectedCourier?.rate ??
        selectedCourier?.price;

      const courierEtd =
        selectedCourier?.etd ??
        selectedCourier?.tatDays;

      // ========================================================
      // BOOKING DATA
      // ========================================================

      const bookingData = {
        action: 'create_order',

        ...formData,

        courier_id:
          courierId,

        courier_name:
          courierName,

        shipping_cost:
          Math.ceil(
            parseFloat(courierRate) + 20
          ),

        delivery_date:
          courierEtd,

        product_description:
          formData.productDescription,

        declared_value:
          parseFloat(
            formData.declaredValue
          ) || 0,

        parcel_type:
          formData.parcelType,

        payment_mode:
          formData.paymentMode === 'COD'
            ? 'cod'
            : 'prepaid',

        payment_id:
          paymentId,

        timestamp:
          new Date().toISOString()
      };

      console.log(
        '🚀 SENDING BOOKING TO N8N:',
        bookingData
      );

      // ========================================================
      // CALL N8N
      // ========================================================

      const bookingRes = await axios.post(
        BACKEND_BASE_URL,
        bookingData
      );

      console.log(
        '🔥 N8N BOOKING RESPONSE:',
        bookingRes.data
      );

      const responseData =
        bookingRes?.data;

      // ========================================================
      // FIND ORDER DATA
      // ========================================================

      const orderData =
        responseData?.data ||
        responseData?.order ||
        responseData?.result ||
        responseData;

      // ========================================================
      // FIND ACTUAL ORDER ID / SHIPMENT ID / AWB
      // ========================================================

      const orderId =
        orderData?.order_id ||
        orderData?.orderId ||
        orderData?.order_number ||
        orderData?.shipment_id ||
        orderData?.shipmentId ||
        orderData?.awb ||
        orderData?.awb_number ||
        orderData?.awbNumber;

      // ========================================================
      // FIND ORDER STATUS
      // ========================================================

      const orderStatus = String(
        orderData?.order_status ||
        orderData?.orderStatus ||
        orderData?.status ||
        responseData?.order_status ||
        responseData?.status ||
        ''
      ).toLowerCase();

      // ========================================================
      // ERROR CHECK
      // ========================================================

      const hasError =
        responseData?.success === false ||
        responseData?.status === false ||
        !!responseData?.error ||
        !!responseData?.errors ||
        orderData?.success === false ||
        !!orderData?.error ||
        !!orderData?.errors;

      // ========================================================
      // SUCCESS STATUS
      // ========================================================

      const successfulStatuses = [
        'success',
        'successful',
        'created',
        'booked',
        'confirmed',
        'pickup_scheduled',
        'pickup scheduled',
        'order_created',
        'order created'
      ];

      const hasSuccessfulStatus =
        successfulStatuses.includes(
          orderStatus
        );

      const explicitSuccess =
        responseData?.success === true ||
        responseData?.status === true ||
        orderData?.success === true;

      // ========================================================
      // FINAL BOOKING SUCCESS
      //
      // IMPORTANT:
      // HTTP 200 alone is NOT success.
      //
      // Payment success alone is NOT success.
      //
      // Actual order/shipment/AWB ID is required.
      // ========================================================

      const orderGenerated =
        !hasError &&
        !!orderId &&
        (
          explicitSuccess ||
          hasSuccessfulStatus
        );

      // ========================================================
      // SUCCESS
      // ========================================================

      if (orderGenerated) {

        console.log(
          '✅ ACTUAL NIMBUSPOST ORDER GENERATED:',
          orderId
        );

        alert(
          `Order Generated Successfully!\n\n` +
          `Your booking has been successfully transferred to NimbusPost.\n\n` +
          `Order ID / Shipment ID: ${orderId}`
        );

        navigate('/dashboard');

      } else {

        // ======================================================
        // FAILURE
        // ======================================================

        console.error(
          '❌ NIMBUSPOST ORDER WAS NOT GENERATED:',
          responseData
        );

        const errorMessage =
          orderData?.error?.message ||
          responseData?.error?.message ||
          orderData?.message ||
          responseData?.message ||
          (
            Array.isArray(
              responseData?.errors
            )
              ? responseData.errors.join(', ')
              : null
          ) ||
          'NimbusPost could not generate the order.';

        alert(
          `Order Generation Failed\n\n${errorMessage}`
        );
      }

    } catch (error) {

      console.error(
        '❌ BOOKING ERROR:',
        error
      );

      console.error(
        '❌ N8N ERROR RESPONSE:',
        error?.response?.data
      );

      const serverError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.errors ||
        error?.message;

      alert(
        `Order Generation Failed\n\n${
          Array.isArray(serverError)
            ? serverError.join(', ')
            : serverError ||
              'Could not connect to the n8n webhook server.'
        }`
      );

    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // PHONE INPUT
  // ============================================================

  const handlePhoneInput = (e, fieldName) => {
    const val = e.target.value
      .replace(/\D/g, '')
      .slice(0, 10);

    setFormData((prev) => ({
      ...prev,
      [fieldName]: val
    }));
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="min-h-screen bg-slate-50 pb-24">

      {/* Header */}
      <div className="bg-[#002D5E] text-white pt-12 pb-24 px-6 md:px-12 relative overflow-hidden">

        <div className="max-w-5xl mx-auto relative z-10">

          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-orange-400 mb-8 font-bold hover:text-orange-300 transition relative z-10 cursor-pointer"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>

          <h1 className="text-3xl md:text-5xl font-black mb-4">
            Book Your Parcel
          </h1>

          <p className="text-slate-300 font-medium max-w-xl">
            Solutions for all delivery "Fastest. Safest. Reliable."
          </p>

        </div>
      </div>

      <div className="max-w-5xl mx-auto -mt-16 px-6 relative z-50">

        <div className="courier-booking-panel bg-white rounded-[1.75rem] sm:rounded-[3rem] shadow-2xl p-5 sm:p-8 md:p-12 border-4 border-orange-50">

          <h2 className="text-2xl sm:text-3xl font-black text-[#002D5E] mb-8 sm:mb-10 flex items-center gap-3 border-b-2 border-slate-100 pb-4">
            <Package
              className="text-orange-500"
              size={32}
            />
            Shipment & Rate Details
          </h2>

          <form
            onSubmit={handleFinalBooking}
            className="courier-contact-form space-y-8 md:space-y-12"
          >

            {/* ==================================================
                1. RATE SECTION
            ================================================== */}

            <div className="bg-slate-50 p-6 md:p-8 rounded-[2rem] border-2 border-slate-100 mb-12">

              <h3 className="text-xl font-black text-[#002D5E] mb-6 flex items-center gap-2">
                <IndianRupee
                  size={24}
                  className="text-blue-600"
                />
                1. Check Rates & Service Availability
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">

                {/* Pickup */}

                <div className="space-y-1">

                  <label className="text-xs font-bold text-slate-500 ml-2">
                    Pickup Pincode
                  </label>

                  <input
                    name="pickupPincode"
                    value={formData.pickupPincode}
                    onChange={(e) =>
                      handlePincodeChange(
                        e,
                        'pickup'
                      )
                    }
                    required
                    maxLength="6"
                    placeholder="Ex. 400001"
                    className="w-full p-4 bg-white rounded-2xl border border-slate-200 font-bold outline-none"
                  />

                  {formData.pickupCity && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">

                      <input
                        name="pickupCity"
                        value={formData.pickupCity}
                        onChange={handleInputChange}
                        placeholder="City"
                        className="w-full p-3 bg-white rounded-xl border border-slate-200 text-xs font-bold outline-none"
                      />

                      <input
                        name="pickupState"
                        value={formData.pickupState}
                        onChange={handleInputChange}
                        placeholder="State"
                        className="w-full p-3 bg-white rounded-xl border border-slate-200 text-xs font-bold outline-none"
                      />

                    </div>
                  )}

                </div>

                {/* Delivery */}

                <div className="space-y-1">

                  <label className="text-xs font-bold text-slate-500 ml-2">
                    Delivery Pincode
                  </label>

                  <input
                    name="dropPincode"
                    value={formData.dropPincode}
                    onChange={(e) =>
                      handlePincodeChange(
                        e,
                        'drop'
                      )
                    }
                    required
                    maxLength="6"
                    placeholder="Ex. 110001"
                    className="w-full p-4 bg-white rounded-2xl border border-slate-200 font-bold outline-none"
                  />

                  {formData.dropCity && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">

                      <input
                        name="dropCity"
                        value={formData.dropCity}
                        onChange={handleInputChange}
                        placeholder="City"
                        className="w-full p-3 bg-white rounded-xl border border-slate-200 text-xs font-bold outline-none"
                      />

                      <input
                        name="dropState"
                        value={formData.dropState}
                        onChange={handleInputChange}
                        placeholder="State"
                        className="w-full p-3 bg-white rounded-xl border border-slate-200 text-xs font-bold outline-none"
                      />

                    </div>
                  )}

                </div>

                {/* Weight */}

                <div className="space-y-1">

                  <label className="text-xs font-bold text-slate-500 ml-2">
                    Approx. Weight (Kg)
                  </label>

                  <input
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    required
                    type="number"
                    step="0.1"
                    placeholder="0.5"
                    className="w-full p-4 bg-white rounded-2xl border border-slate-200 font-bold outline-none"
                  />

                </div>

              </div>

              {/* Dimensions */}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 items-end mb-6">

                {[
                  'length',
                  'breadth',
                  'height'
                ].map((field) => (

                  <div
                    key={field}
                    className="space-y-1"
                  >

                    <label className="text-xs font-bold text-slate-500 ml-2 flex items-center gap-1">

                      <Ruler
                        size={14}
                        className="text-orange-500"
                      />

                      {field.charAt(0).toUpperCase() +
                        field.slice(1)} (cm)

                    </label>

                    <input
                      name={field}
                      value={formData[field]}
                      onChange={handleInputChange}
                      required
                      type="number"
                      step="0.1"
                      placeholder="10"
                      className="w-full p-4 bg-white rounded-2xl border border-slate-200 font-bold outline-none"
                    />

                  </div>

                ))}

              </div>

              {/* Product */}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 mb-6">

                <div className="space-y-1">

                  <label className="text-xs font-bold text-slate-500 ml-2">
                    Product Description
                  </label>

                  <input
                    name="productDescription"
                    value={formData.productDescription}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex. Masala, Clothes, Electronics"
                    className="w-full p-4 bg-white rounded-2xl border border-slate-200 font-bold outline-none"
                  />

                </div>

                <div className="space-y-1">

                  <label className="text-xs font-bold text-slate-500 ml-2">
                    Declared Value (₹)
                  </label>

                  <input
                    name="declaredValue"
                    value={formData.declaredValue}
                    onChange={handleInputChange}
                    required
                    type="number"
                    min="1"
                    placeholder="Ex. 2000"
                    className="w-full p-4 bg-white rounded-2xl border border-slate-200 font-bold outline-none"
                  />

                </div>

              </div>

              <div className="flex justify-end">

                <button
                  type="button"
                  onClick={handleCheckLiveRates}
                  disabled={rateCheckLoading}
                  className="w-full sm:w-auto px-8 py-4 h-[56px] bg-blue-600 text-white rounded-2xl font-black uppercase tracking-wider hover:bg-blue-700 transition flex items-center justify-center gap-2 cursor-pointer"
                >

                  {rateCheckLoading ? (
                    <RefreshCcw
                      size={20}
                      className="animate-spin"
                    />
                  ) : (
                    <Search size={20} />
                  )}

                  {rateCheckLoading
                    ? 'Checking Rates...'
                    : 'Check Rates'}

                </button>

              </div>

              {/* Rates */}

              {rates && (

                <div className="mt-8 bg-white p-6 rounded-2xl border border-green-100">

                  <h4 className="font-black text-green-800 uppercase text-sm mb-4">
                    Available Courier Companies:
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">

                    {rates.map((courier, idx) => {

                      const courierCompanyId =
                        courier.courier_company_id ||
                        courier.courierId;

                      const courierName =
                        courier.courier_name ||
                        courier.courierName;

                      const courierRate =
                        courier.rate ??
                        courier.price;

                      const courierEtd =
                        courier.etd ??
                        courier.tatDays;

                      const isSelected =
                        selectedCourier?.courier_company_id ===
                          courierCompanyId ||
                        selectedCourier?.courierId ===
                          courierCompanyId;

                      return (

                        <div
                          key={idx}
                          onClick={() =>
                            setSelectedCourier(courier)
                          }
                          className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-[#002D5E] text-white border-orange-400 shadow-lg scale-[1.02]'
                              : 'bg-slate-50 border-slate-100'
                          }`}
                        >

                          <p className="font-black text-sm">
                            {courierName}
                          </p>

                          <p className="text-xs mt-1 opacity-80">
                            Est. Delivery:{' '}
                            {courierEtd || 'N/A'}
                          </p>

                          <p className="text-2xl font-black mt-3 text-orange-400">
                            ₹
                            {Math.ceil(
                              parseFloat(courierRate) + 20
                            )}
                          </p>

                        </div>

                      );
                    })}

                  </div>

                </div>

              )}

            </div>

            {/* ==================================================
                2. SENDER / RECEIVER
            ================================================== */}

            <div className="pt-6 sm:pt-8 border-t-2 border-slate-100">

              <h3 className="text-lg sm:text-xl font-black text-[#002D5E] mb-4 sm:mb-6 flex items-center gap-2">

                <CheckCircle2
                  size={24}
                  className="text-green-500"
                />

                2. Sender and Receiver Details

              </h3>

              <div className="grid min-w-0 grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">

                {/* Sender */}

                <div className="courier-contact-card min-w-0 space-y-4 bg-blue-50/50 p-4 sm:p-5 md:p-6 rounded-2xl border border-blue-100">

                  <h4 className="text-base sm:text-lg font-black text-blue-900 flex items-center gap-2">
                    <User size={18} />
                    Sender (From)
                  </h4>

                  <input
                    name="senderName"
                    value={formData.senderName}
                    onChange={handleInputChange}
                    required
                    placeholder="Full Name"
                    className="courier-contact-input"
                  />

                  <div className="courier-phone-field">

                    <span className="courier-country-code">
                      +91
                    </span>

                    <input
                      name="senderPhone"
                      value={formData.senderPhone}
                      required
                      type="tel"
                      maxLength="10"
                      placeholder="Mobile Number"
                      className="courier-phone-input"
                      onChange={(e) =>
                        handlePhoneInput(
                          e,
                          'senderPhone'
                        )
                      }
                    />

                  </div>

                  <input
                    name="pickupPincode"
                    value={formData.pickupPincode}
                    onChange={(e) =>
                      handlePincodeChange(
                        e,
                        'pickup'
                      )
                    }
                    required
                    maxLength="6"
                    placeholder="Pickup Pincode"
                    className="courier-contact-input"
                  />

                  {formData.pickupCity && (

                    <div className="grid grid-cols-2 gap-2">

                      <input
                        name="pickupCity"
                        value={formData.pickupCity}
                        onChange={handleInputChange}
                        placeholder="City"
                        className="courier-contact-input"
                      />

                      <input
                        name="pickupState"
                        value={formData.pickupState}
                        onChange={handleInputChange}
                        placeholder="State"
                        className="courier-contact-input"
                      />

                    </div>

                  )}

                  <textarea
                    name="senderAddress"
                    value={formData.senderAddress}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    placeholder="Address"
                    className="courier-contact-input"
                  />

                </div>

                {/* Receiver */}

                <div className="courier-contact-card min-w-0 space-y-4 bg-orange-50/50 p-4 sm:p-5 md:p-6 rounded-2xl border border-orange-100">

                  <h4 className="text-base sm:text-lg font-black text-orange-900 flex items-center gap-2">

                    <MapPin size={18} />

                    Receiver (To)

                  </h4>

                  <input
                    name="receiverName"
                    value={formData.receiverName}
                    onChange={handleInputChange}
                    required
                    placeholder="Full Name"
                    className="courier-contact-input"
                  />

                  <div className="courier-phone-field">

                    <span className="courier-country-code">
                      +91
                    </span>

                    <input
                      name="receiverPhone"
                      value={formData.receiverPhone}
                      required
                      type="tel"
                      maxLength="10"
                      placeholder="Mobile Number"
                      className="courier-phone-input"
                      onChange={(e) =>
                        handlePhoneInput(
                          e,
                          'receiverPhone'
                        )
                      }
                    />

                  </div>

                  <input
                    name="dropPincode"
                    value={formData.dropPincode}
                    onChange={(e) =>
                      handlePincodeChange(
                        e,
                        'drop'
                      )
                    }
                    required
                    maxLength="6"
                    placeholder="Delivery Pincode"
                    className="courier-contact-input"
                  />

                  {formData.dropCity && (

                    <div className="grid grid-cols-2 gap-2">

                      <input
                        name="dropCity"
                        value={formData.dropCity}
                        onChange={handleInputChange}
                        placeholder="City"
                        className="courier-contact-input"
                      />

                      <input
                        name="dropState"
                        value={formData.dropState}
                        onChange={handleInputChange}
                        placeholder="State"
                        className="courier-contact-input"
                      />

                    </div>

                  )}

                  <textarea
                    name="receiverAddress"
                    value={formData.receiverAddress}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    placeholder="Address"
                    className="courier-contact-input"
                  />

                </div>

              </div>

            </div>

            {/* ==================================================
                PAYMENT + BOOKING
            ================================================== */}

            <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-6 border-t border-slate-100">

              <div className="flex items-center gap-4">

                <label className="text-sm font-black text-slate-500 uppercase">
                  Payment Mode:
                </label>

                <div className="flex gap-2">

                  {[
                    'Prepaid',
                    'COD'
                  ].map((mode) => (

                    <button
                      key={mode}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          paymentMode: mode
                        }))
                      }
                      className={`px-6 py-2 rounded-full font-black text-xs uppercase cursor-pointer ${
                        formData.paymentMode === mode
                          ? 'bg-[#002D5E] text-white'
                          : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      {mode}
                    </button>

                  ))}

                </div>

              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full md:w-auto px-6 sm:px-12 py-5 bg-orange-500 text-white rounded-2xl font-black uppercase tracking-wider md:tracking-[0.2em] shadow-xl hover:bg-orange-600 transition flex items-center justify-center gap-3 cursor-pointer"
              >

                {loading
                  ? 'Processing...'
                  : 'Confirm Booking'}

                <ChevronRight size={20} />

              </button>

            </div>

          </form>

          {/* Truck */}

          <div className="mt-14 text-center pt-8 border-t-2 border-slate-100">

            <img
              src="/truck.png"
              alt="Delivery Truck"
              className="mx-auto w-full max-w-2xl max-h-96 object-contain rounded-3xl shadow-xl hover:scale-102 transition-transform duration-300"
            />

          </div>

        </div>
      </div>
    </div>
  );
};

export default CourierServiceDetail;